(() => {
  if (customElements.get('latrand-banner-slides')) return;

  class LatrandBannerSlides extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.viewport = this.querySelector('[data-banner-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-banner-slide]'));
      if (!this.viewport || !this.slides.length) return;

      this.previousButton = this.querySelector('[data-banner-previous]');
      this.nextButton = this.querySelector('[data-banner-next]');
      this.controls = this.querySelector('[data-banner-controls]');
      this.autoplayButton = this.querySelector('[data-banner-autoplay-toggle]');
      this.status = this.querySelector('[data-banner-status]');
      this.activeIndex = Math.min(this.activeIndex || 0, this.slides.length - 1);
      this.initialized = true;
      this.abortController = new AbortController();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayEnabled = this.dataset.bannerSlot === 'main' &&
        this.dataset.autoplay === 'true' && this.slides.length > 1;

      const seconds = Number(this.dataset.autoplayInterval);
      this.interval = Math.min(20, Math.max(3,
        Number.isFinite(seconds) && seconds > 0 ? seconds : 6)) * 1000;
      this.userPaused = this.userPaused || false;
      this.motionOverride = false;
      this.hovered = this.matches(':hover') && window.matchMedia('(hover: hover)').matches;
      this.focusWithin = this.contains(document.activeElement);
      this.touchActive = false;
      this.activePointers = new Set();
      this.editorSelected = false;
      this.inView = !('IntersectionObserver' in window);

      const options = { signal: this.abortController.signal };
      const passiveOptions = { ...options, passive: true };

      if (this.controls) this.controls.hidden = this.slides.length < 2;
      if (!this.viewport.hasAttribute('tabindex')) this.viewport.setAttribute('tabindex', '0');
      this.previousButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1), options);
      this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1), options);
      this.addEventListener('keydown', (event) => this.onKeydown(event), options);

      if (this.autoplayButton) {
        this.autoplayButton.hidden = !this.autoplayEnabled;
        this.autoplayButton.disabled = !this.autoplayEnabled;
        this.autoplayButton.addEventListener('click', () => {
          if (!this.autoplayEnabled) return;
          const paused = this.isPlaybackPaused();
          this.userPaused = !paused;
          this.motionOverride = paused;
          this.updateAutoplayButton();
          this.scheduleAutoplay();
        }, options);
      }

      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.hovered = true;
        this.scheduleAutoplay();
      }, options);
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.scheduleAutoplay();
      }, options);
      this.addEventListener('focusin', () => {
        this.focusWithin = true;
        this.scheduleAutoplay();
      }, options);
      this.addEventListener('focusout', (event) => {
        this.focusWithin = this.contains(event.relatedTarget);
        this.scheduleAutoplay();
      }, options);
      this.viewport.addEventListener('pointerdown', (event) => {
        this.activePointers.add(event.pointerId);
        this.scheduleAutoplay();
      }, passiveOptions);
      const releasePointer = (event) => {
        if (!this.activePointers.delete(event.pointerId)) return;
        this.scheduleAutoplay();
      };
      window.addEventListener('pointerup', releasePointer, passiveOptions);
      window.addEventListener('pointercancel', releasePointer, passiveOptions);
      this.viewport.addEventListener('touchstart', () => {
        this.touchActive = true;
        this.scheduleAutoplay();
      }, passiveOptions);
      const releaseTouch = (event) => {
        if (!this.touchActive) return;
        this.touchActive = event.touches.length > 0;
        this.scheduleAutoplay();
      };
      window.addEventListener('touchend', releaseTouch, passiveOptions);
      window.addEventListener('touchcancel', releaseTouch, passiveOptions);
      document.addEventListener('visibilitychange', () => this.scheduleAutoplay(), options);

      this.onMotionChange = () => {
        this.motionOverride = false;
        this.updateAutoplayButton();
        this.scheduleAutoplay();
      };
      if (this.reducedMotion.addEventListener) {
        this.reducedMotion.addEventListener('change', this.onMotionChange, options);
      } else {
        this.reducedMotion.addListener(this.onMotionChange);
      }

      const slideForSelection = (event) => {
        const blockId = event.detail?.blockId;
        return this.slides.find((slide) => blockId && slide.dataset.bannerBlockId === blockId) ||
          event.target?.closest?.('[data-banner-slide]');
      };
      const selectionBelongsHere = (event) => this.slides.includes(slideForSelection(event)) ||
        this.contains(event.target) || event.target?.contains?.(this);
      const onEditorSelect = (event) => {
        if (!selectionBelongsHere(event)) return;
        this.editorSelected = true;
        const selectedSlide = slideForSelection(event);
        const index = this.slides.indexOf(selectedSlide);
        if (index >= 0) this.goTo(index);
        else this.scheduleAutoplay();
      };
      const onEditorDeselect = (event) => {
        if (!selectionBelongsHere(event)) return;
        this.editorSelected = false;
        this.scheduleAutoplay();
      };
      document.addEventListener('shopify:block:select', onEditorSelect, options);
      document.addEventListener('shopify:block:deselect', onEditorDeselect, options);
      document.addEventListener('shopify:section:select', onEditorSelect, options);
      document.addEventListener('shopify:section:deselect', onEditorDeselect, options);

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          const entry = entries.find((item) => item.target === this);
          if (!entry) return;
          this.inView = entry.isIntersecting && entry.intersectionRatio > 0;
          this.scheduleAutoplay();
        }, { threshold: [0, 0.01] });
        this.visibilityObserver.observe(this);
      }

      this.update(this.activeIndex, false);
      this.setAttribute('data-banner-ready', 'true');
      this.updateAutoplayButton();
      this.scheduleAutoplay();
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.initialized = false;
      this.stopAutoplay();
      this.abortController.abort();
      this.visibilityObserver?.disconnect();
      if (!this.reducedMotion.addEventListener) {
        this.reducedMotion.removeListener(this.onMotionChange);
      }
      this.activePointers.clear();
    }

    isPlaybackPaused() {
      return this.userPaused || (this.reducedMotion.matches && !this.motionOverride);
    }

    canAutoplay() {
      return this.initialized && this.isConnected && this.autoplayEnabled &&
        !this.isPlaybackPaused() && this.inView && !document.hidden &&
        !this.hovered && !this.focusWithin && !this.touchActive &&
        !this.activePointers.size && !this.editorSelected;
    }

    stopAutoplay() {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    scheduleAutoplay() {
      this.stopAutoplay();
      if (!this.canAutoplay()) return;
      this.autoplayTimer = window.setTimeout(() => {
        this.autoplayTimer = null;
        if (this.canAutoplay()) this.goTo(this.activeIndex + 1, false);
      }, this.interval);
    }

    updateAutoplayButton() {
      if (!this.autoplayButton) return;
      const paused = this.isPlaybackPaused();
      const label = paused
        ? this.autoplayButton.dataset.playLabel || this.dataset.playLabel || 'Пуснете автоматичната смяна'
        : this.autoplayButton.dataset.pauseLabel || this.dataset.pauseLabel || 'Спрете автоматичната смяна';
      this.autoplayButton.dataset.paused = String(paused);
      this.autoplayButton.setAttribute('aria-label', label);
      this.autoplayButton.setAttribute('title', label);
      const visibleLabel = this.autoplayButton.querySelector('[data-banner-autoplay-label]');
      if (visibleLabel) visibleLabel.textContent = label;
    }

    onKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey ||
        (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') || this.slides.length < 2) return;
      if (event.target?.closest?.(
        'input, select, textarea, button, [contenteditable]:not([contenteditable="false"]), ' +
        '[role="textbox"], [role="combobox"], [role="slider"], [role="spinbutton"], ' +
        '[role="menu"], [role="listbox"], [role="tablist"], [role="radiogroup"]'
      )) return;
      event.preventDefault();
      const rtl = window.getComputedStyle(this.viewport).direction === 'rtl';
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      this.goTo(this.activeIndex + (rtl ? -direction : direction));
    }

    goTo(index, announce = true) {
      if (!this.initialized || !Number.isFinite(index)) return;
      const nextIndex = ((Math.trunc(index) % this.slides.length) + this.slides.length) % this.slides.length;
      const activeSlide = this.slides[this.activeIndex];
      const moveFocus = nextIndex !== this.activeIndex && activeSlide.contains(document.activeElement);
      this.update(nextIndex, announce);
      if (moveFocus) this.viewport.focus({ preventScroll: true });
      this.scheduleAutoplay();
    }

    update(index, announce) {
      this.activeIndex = index;
      this.slides.forEach((slide, slideIndex) => {
        const inactive = slideIndex !== index;
        slide.hidden = inactive;
        slide.inert = inactive;
        slide.setAttribute('aria-hidden', String(inactive));
      });
      if (this.previousButton) this.previousButton.disabled = this.slides.length < 2;
      if (this.nextButton) this.nextButton.disabled = this.slides.length < 2;
      if (this.status) {
        this.status.setAttribute('aria-live', announce ? 'polite' : 'off');
        this.status.setAttribute('aria-atomic', 'true');
        this.status.textContent = `${index + 1} / ${this.slides.length}`;
      }
    }
  }

  customElements.define('latrand-banner-slides', LatrandBannerSlides);
})();
